import { GameState, CardDef, PlayerSide, GameAction, AttackGroup } from '../types.js';
import { getPlayerState, getOpponentSide, getInstance, getCardDef } from './GameState.js';
import { canAfford } from './SpiritManager.js';
import { getConfig } from '../config.js';
import { getRalliesPerTurn, getSupportsPerTurn, getMaxHandSize, hasPassive, hasAnyPassiveInRing } from './AbilityProcessor.js';

export function canPlayCharacter(
  state: GameState,
  instanceId: string,
  slotIndex: number,
  catalog: Map<string, CardDef>,
): boolean {
  const side = state.currentPlayer;
  const ps = getPlayerState(state, side);
  if (state.phase !== 'main') return false;
  if (ps.ralliesUsed >= getRalliesPerTurn(state, side, catalog)) return false;
  if (!ps.hand.includes(instanceId)) return false;

  const inst = getInstance(state, instanceId);
  const def = getCardDef(inst.cardId, catalog);
  if (def.type !== 'character') return false;

  // Check spirit cost
  if (def.spirit < 0 && !canAfford(ps.spirit, Math.abs(def.spirit))) return false;

  // Check play-clock threshold
  // Cards with clock=1 (the implicit default — these are the cards that
  // don't even render a clock chip) are playable from turn 1, when
  // state.clock is still 0. Treat the effective threshold as
  // max(state.clock, 1) so clock-1 cards always pass and clock-N>=2
  // cards still need state.clock to have caught up.
  if ((def.clock ?? 1) > Math.max(state.clock, 1)) return false;

  // Check slot is empty
  if (slotIndex < 0 || slotIndex >= ps.ring.length) return false;
  if (ps.ring[slotIndex].instanceId !== null) return false;

  // No duplicate characters (same cardId) already in ring
  for (const slot of ps.ring) {
    if (slot.instanceId) {
      const ringInst = getInstance(state, slot.instanceId);
      if (ringInst.cardId === inst.cardId) return false;
    }
  }

  return true;
}

export function canSupport(
  state: GameState,
  handInstanceId: string,
  ringInstanceId: string,
  catalog: Map<string, CardDef>,
  /** UI predictive flag. When true, skip the "have you used all your
   *  supports this turn?" check so the caller can render a faint
   *  "compatible but unavailable" highlight that still helps the
   *  player see tag-compatibility relationships after they've spent
   *  their supports. All other gates (phase, zone, fresh, exhausted,
   *  tag match) still apply. */
  options?: { ignoreSupportsUsed?: boolean },
): boolean {
  const side = state.currentPlayer;
  const ps = getPlayerState(state, side);
  if (state.phase !== 'main') return false;
  if (!options?.ignoreSupportsUsed && ps.supportsUsed >= getSupportsPerTurn(state, side, catalog)) return false;
  if (!ps.hand.includes(handInstanceId)) return false;

  const handInst = getInstance(state, handInstanceId);
  const handDef = getCardDef(handInst.cardId, catalog);
  if (handDef.type !== 'character') return false;

  const ringInst = getInstance(state, ringInstanceId);
  const ringDef = getCardDef(ringInst.cardId, catalog);
  if (ringDef.type !== 'character') return false;

  // Ring character must be in ring and not fresh/exhausted
  if (ringInst.zone !== 'ring') return false;
  if (ringInst.isFresh) return false;
  if (ringInst.isExhausted) return false;

  // Must share at least 2 tags (or 1 if either card has passive-easy-support in ring)
  const sharedCount = handDef.tags.filter(t => ringDef.tags.includes(t)).length;
  let requiredTags = 2;

  // Check if any ring character has passive-easy-support, lowering the threshold
  const ps2 = getPlayerState(state, side);
  for (const slot of ps2.ring) {
    if (!slot.instanceId) continue;
    const ri = getInstance(state, slot.instanceId);
    if (ri.abilityDisabled) continue;
    const rd = catalog.get(ri.cardId);
    if (rd && rd.type === 'character' && rd.ability?.type === 'passive' && rd.ability.effect === 'passive-easy-support') {
      requiredTags = 1;
      break;
    }
  }

  if (sharedCount < requiredTags) return false;

  return true;
}

/** UI-only predictive check: would `supporterHandId` be able to support
 *  `hoveredHandId` if `hoveredHandId` were on the ring right now? Mirrors
 *  the tag-match / supports-used / passive-easy-support logic of
 *  `canSupport` but treats `hoveredHandId` as if it had already been
 *  played (skips the ring / fresh / exhausted gating). Used by the
 *  Renderer to glow compatible hand cards while the player hovers
 *  another hand card, helping them plan a play + support sequence. */
export function wouldSupportIfPlayed(
  state: GameState,
  supporterHandId: string,
  hoveredHandId: string,
  catalog: Map<string, CardDef>,
  /** Same UI predictive flag as `canSupport` — see the doc there. */
  options?: { ignoreSupportsUsed?: boolean },
): boolean {
  if (supporterHandId === hoveredHandId) return false;
  if (state.phase !== 'main') return false;
  const side = state.currentPlayer;
  const ps = getPlayerState(state, side);
  if (!options?.ignoreSupportsUsed && ps.supportsUsed >= getSupportsPerTurn(state, side, catalog)) return false;
  if (!ps.hand.includes(supporterHandId)) return false;
  if (!ps.hand.includes(hoveredHandId)) return false;

  const supporterInst = getInstance(state, supporterHandId);
  const supporterDef = getCardDef(supporterInst.cardId, catalog);
  if (supporterDef.type !== 'character') return false;

  const hoveredInst = getInstance(state, hoveredHandId);
  const hoveredDef = getCardDef(hoveredInst.cardId, catalog);
  if (hoveredDef.type !== 'character') return false;

  const sharedCount = supporterDef.tags.filter(t => hoveredDef.tags.includes(t)).length;
  let requiredTags = 2;
  for (const slot of ps.ring) {
    if (!slot.instanceId) continue;
    const ri = getInstance(state, slot.instanceId);
    if (ri.abilityDisabled) continue;
    const rd = catalog.get(ri.cardId);
    if (rd && rd.type === 'character' && rd.ability?.type === 'passive' && rd.ability.effect === 'passive-easy-support') {
      requiredTags = 1;
      break;
    }
  }
  return sharedCount >= requiredTags;
}

export function canActivateAbility(
  state: GameState,
  instanceId: string,
  catalog: Map<string, CardDef>,
): boolean {
  if (state.phase !== 'main') return false;

  const inst = getInstance(state, instanceId);
  if (inst.zone !== 'ring') return false;
  if (inst.isFresh) return false;
  if (inst.isExhausted) return false;
  if (inst.owner !== state.currentPlayer) return false;

  const def = getCardDef(inst.cardId, catalog);
  if (def.type !== 'character') return false;
  if (def.ability?.type !== 'activated') return false;

  // Per-instance ability lockdown — the silence verb (and any future
  // disabling effect) flips this flag and the activated ability should
  // stop responding immediately. CardDef is intact (so the card still
  // describes itself in tooltips etc.) but the live instance can no
  // longer fire its ability.
  if (inst.abilityDisabled) return false;

  // Global lockdown passive — disables every character's activated ability,
  // including the disabling character's own (if it had one, but it can't:
  // each character has a single ability and this passive occupies that slot).
  if (hasAnyPassiveInRing(state, 'passive-disable-activated', catalog)) return false;

  // Block activation while over hand limit — some activated abilities draw,
  // and activating while already overhand just digs the hole deeper.
  const ps = getPlayerState(state, state.currentPlayer);
  if (ps.hand.length > getMaxHandSize(state, state.currentPlayer, catalog)) return false;

  return true;
}

export function canPlayAction(
  state: GameState,
  instanceId: string,
  catalog: Map<string, CardDef>,
): boolean {
  const side = state.currentPlayer;
  const ps = getPlayerState(state, side);
  if (state.phase !== 'main') return false;
  if (!ps.hand.includes(instanceId)) return false;

  const inst = getInstance(state, instanceId);
  const def = getCardDef(inst.cardId, catalog);
  if (def.type !== 'action') return false;

  if (def.spirit < 0 && !canAfford(ps.spirit, Math.abs(def.spirit))) return false;
  // Cards with clock=1 (the implicit default — these are the cards that
  // don't even render a clock chip) are playable from turn 1, when
  // state.clock is still 0. Treat the effective threshold as
  // max(state.clock, 1) so clock-1 cards always pass and clock-N>=2
  // cards still need state.clock to have caught up.
  if ((def.clock ?? 1) > Math.max(state.clock, 1)) return false;

  return true;
}

export function canPlayReaction(
  state: GameState,
  instanceId: string,
  catalog: Map<string, CardDef>,
): boolean {
  const defenderSide = getOpponentSide(state.currentPlayer);
  const ps = getPlayerState(state, defenderSide);
  if (state.phase !== 'counter') return false;
  if (!ps.hand.includes(instanceId)) return false;

  const inst = getInstance(state, instanceId);
  const def = getCardDef(inst.cardId, catalog);
  if (def.type !== 'action') return false;
  if (def.subtype !== 'reaction') return false;

  if (def.spirit < 0 && !canAfford(ps.spirit, Math.abs(def.spirit))) return false;
  // Cards with clock=1 (the implicit default — these are the cards that
  // don't even render a clock chip) are playable from turn 1, when
  // state.clock is still 0. Treat the effective threshold as
  // max(state.clock, 1) so clock-1 cards always pass and clock-N>=2
  // cards still need state.clock to have caught up.
  if ((def.clock ?? 1) > Math.max(state.clock, 1)) return false;

  return true;
}

export function canDeclareAttackers(
  state: GameState,
  groups: AttackGroup[],
): boolean {
  if (state.phase !== 'attack') return false;
  if (groups.length === 0) return false;

  const side = state.currentPlayer;
  const ps = getPlayerState(state, side);
  const config = getConfig();

  let totalUnitedCost = 0;

  for (const group of groups) {
    if (group.attackerIds.length === 0) return false;
    for (const id of group.attackerIds) {
      const inst = getInstance(state, id);
      if (inst.zone !== 'ring') return false;
      if (inst.owner !== side) return false;
      if (inst.isFresh) return false;
      if (inst.isExhausted) return false;
    }
    if (group.united && group.attackerIds.length >= 2) {
      totalUnitedCost += group.attackerIds.length === 2 ? config.unitedAttackCost2 : config.unitedAttackCost3;
    }
  }

  if (totalUnitedCost > 0 && !canAfford(ps.spirit, totalUnitedCost)) return false;

  return true;
}

export function canAssignBlocker(
  state: GameState,
  groupIndex: number,
  blockerInstanceId: string,
  catalog: Map<string, CardDef>,
): boolean {
  if (state.phase !== 'counter') return false;
  if (!state.combatState) return false;

  const defenderSide = getOpponentSide(state.currentPlayer);
  const blockerInst = getInstance(state, blockerInstanceId);
  if (blockerInst.owner !== defenderSide) return false;
  if (blockerInst.zone !== 'ring') return false;
  if (blockerInst.isExhausted) return false;
  if (hasPassive(state, blockerInstanceId, 'passive-cannot-block', catalog)) return false;

  // Group must exist
  if (groupIndex < 0 || groupIndex >= state.combatState.groups.length) return false;

  // Blocker not already assigned to another group
  for (const [gIdx, bId] of state.combatState.blockerAssignments) {
    if (bId === blockerInstanceId && gIdx !== groupIndex) return false;
  }

  return true;
}

// ── Task-list aggregator helpers ────────────────────────────────────
// Small combinators over the single-action canX functions above. These power
// the task-list UI: "can the current player do X right now at all?".

export function hasPlayableCharacter(
  state: GameState,
  catalog: Map<string, CardDef>,
): boolean {
  const ps = getPlayerState(state, state.currentPlayer);
  for (const handId of ps.hand) {
    for (let s = 0; s < ps.ring.length; s++) {
      if (canPlayCharacter(state, handId, s, catalog)) return true;
    }
  }
  return false;
}

export function hasSupportableCombination(
  state: GameState,
  catalog: Map<string, CardDef>,
): boolean {
  const ps = getPlayerState(state, state.currentPlayer);
  for (const handId of ps.hand) {
    for (const slot of ps.ring) {
      if (slot.instanceId && canSupport(state, handId, slot.instanceId, catalog)) return true;
    }
  }
  return false;
}

export function getEligibleAttackers(state: GameState): string[] {
  const ps = getPlayerState(state, state.currentPlayer);
  const result: string[] = [];
  for (const slot of ps.ring) {
    if (!slot.instanceId) continue;
    const inst = getInstance(state, slot.instanceId);
    if (!inst.isFresh && !inst.isExhausted) result.push(slot.instanceId);
  }
  return result;
}

export function hasPlayableAction(
  state: GameState,
  catalog: Map<string, CardDef>,
): boolean {
  const ps = getPlayerState(state, state.currentPlayer);
  for (const handId of ps.hand) {
    const inst = getInstance(state, handId);
    const def = catalog.get(inst.cardId);
    if (!def || def.type !== 'action') continue;
    if (canPlayAction(state, handId, catalog)) return true;
  }
  return false;
}

/** True if the side NOT currently acting has at least one character that
 *  could block an attack this combat — on ring, not exhausted, and not
 *  `passive-cannot-block`. Fresh (summoning-sick) characters CAN block, so
 *  they count. Used to prune strictly-dominated united attacks from the AI
 *  search: a united attack only adds value by piercing a blocker, so with no
 *  eligible blocker it deals the same total damage as the attackers hitting
 *  individually while wasting the united spirit cost. */
export function defenderHasEligibleBlocker(state: GameState, catalog: Map<string, CardDef>): boolean {
  const defenderSide = getOpponentSide(state.currentPlayer);
  const ps = getPlayerState(state, defenderSide);
  for (const slot of ps.ring) {
    if (!slot.instanceId) continue;
    const inst = getInstance(state, slot.instanceId);
    if (inst.isExhausted) continue;
    if (hasPassive(state, slot.instanceId, 'passive-cannot-block', catalog)) continue;
    return true;
  }
  return false;
}

export function canBlockAnyAttacker(state: GameState, catalog: Map<string, CardDef>): boolean {
  if (state.phase !== 'counter' || !state.combatState) return false;
  const defenderSide = getOpponentSide(state.currentPlayer);
  const ps = getPlayerState(state, defenderSide);

  // At least one attacker group must lack a blocker assignment.
  let anyUnblocked = false;
  for (let g = 0; g < state.combatState.groups.length; g++) {
    if (!state.combatState.blockerAssignments.has(g)) { anyUnblocked = true; break; }
  }
  if (!anyUnblocked) return false;

  // Defender must have at least one ring character not yet assigned, not exhausted, and not cannot-block.
  const assignedIds = new Set(state.combatState.blockerAssignments.values());
  for (const slot of ps.ring) {
    if (!slot.instanceId) continue;
    if (assignedIds.has(slot.instanceId)) continue;
    const inst = getInstance(state, slot.instanceId);
    if (inst.isExhausted) continue;
    if (hasPassive(state, slot.instanceId, 'passive-cannot-block', catalog)) continue;
    return true;
  }
  return false;
}

export function hasPlayableReaction(
  state: GameState,
  catalog: Map<string, CardDef>,
): boolean {
  const defenderSide = getOpponentSide(state.currentPlayer);
  const ps = getPlayerState(state, defenderSide);
  for (const handId of ps.hand) {
    if (canPlayReaction(state, handId, catalog)) return true;
  }
  return false;
}

export function mustDiscard(
  state: GameState,
  side: PlayerSide,
  catalog: Map<string, CardDef>,
): number {
  const ps = getPlayerState(state, side);
  return Math.max(0, ps.hand.length - getMaxHandSize(state, side, catalog));
}

export function getLegalActions(
  state: GameState,
  catalog: Map<string, CardDef>,
): GameAction[] {
  const actions: GameAction[] = [];
  const side = state.currentPlayer;
  const ps = getPlayerState(state, side);
  const opSide = getOpponentSide(side);
  const ops = getPlayerState(state, opSide);

  if (state.winner) return [];

  switch (state.phase) {
    case 'main': {
      // Play characters
      for (const handId of ps.hand) {
        for (let s = 0; s < ps.ring.length; s++) {
          if (canPlayCharacter(state, handId, s, catalog)) {
            actions.push({ type: 'PLAY_CHARACTER', instanceId: handId, slotIndex: s });
          }
        }
      }
      // Support
      for (const handId of ps.hand) {
        for (const slot of ps.ring) {
          if (slot.instanceId && canSupport(state, handId, slot.instanceId, catalog)) {
            actions.push({ type: 'SUPPORT_CHARACTER', handInstanceId: handId, ringInstanceId: slot.instanceId });
          }
        }
      }
      // Activate abilities
      for (const slot of ps.ring) {
        if (slot.instanceId && canActivateAbility(state, slot.instanceId, catalog)) {
          actions.push({ type: 'ACTIVATE_ABILITY', instanceId: slot.instanceId });
        }
      }
      // Play action cards
      for (const handId of ps.hand) {
        if (canPlayAction(state, handId, catalog)) {
          actions.push({ type: 'PLAY_ACTION', instanceId: handId });
        }
      }
      // Can always end main phase
      actions.push({ type: 'SKIP_PHASE' });
      break;
    }

    case 'attack': {
      // Find all eligible attackers
      const eligible = ps.ring
        .filter(s => s.instanceId !== null)
        .map(s => s.instanceId!)
        .filter(id => {
          const inst = getInstance(state, id);
          return !inst.isFresh && !inst.isExhausted;
        });

      // Generate all possible group combinations for the AI.
      //
      // Individual (non-united) attacks: EVERY non-empty subset of eligible
      // attackers, each attacking on its own. This spans solo attacks, the
      // all-out attack, and — crucially — partial attacks that hold some
      // characters back (e.g. attack with 2 of 3, keep 1 as a blocker).
      // Without the partial subsets the AI could only express "attack with
      // 2, keep 1 back" as a UNITED attack, so it would pay the united
      // spirit cost even with no blocker to pierce (united attacks only add
      // value against a blocker). Enumerating all subsets is cheap:
      // `eligible` is bounded by ringSlots (3), so this is ≤ 2^3-1 = 7 actions.
      const n = eligible.length;
      for (let mask = 1; mask < (1 << n); mask++) {
        const groups: AttackGroup[] = [];
        for (let i = 0; i < n; i++) {
          if (mask & (1 << i)) groups.push({ attackerIds: [eligible[i]], united: false });
        }
        actions.push({ type: 'DECLARE_ATTACKERS', groups });
      }

      // United attacks (2 or 3)
      const config = getConfig();
      if (eligible.length >= 2) {
        for (let i = 0; i < eligible.length; i++) {
          for (let j = i + 1; j < eligible.length; j++) {
            const unitedIds = [eligible[i], eligible[j]];
            if (canAfford(ps.spirit, config.unitedAttackCost2)) {
              // Just the united pair
              actions.push({ type: 'DECLARE_ATTACKERS', groups: [{ attackerIds: unitedIds, united: true }] });
              // United pair + remaining solos
              const remaining = eligible.filter(id => !unitedIds.includes(id));
              if (remaining.length > 0) {
                const groups: AttackGroup[] = [
                  { attackerIds: unitedIds, united: true },
                  ...remaining.map(id => ({ attackerIds: [id], united: false })),
                ];
                actions.push({ type: 'DECLARE_ATTACKERS', groups });
              }
            }
          }
        }
      }
      if (eligible.length === 3) {
        if (canAfford(ps.spirit, config.unitedAttackCost3)) {
          actions.push({ type: 'DECLARE_ATTACKERS', groups: [{ attackerIds: eligible, united: true }] });
        }
      }

      // Skip attack
      actions.push({ type: 'SKIP_PHASE' });
      break;
    }

    case 'cleanup': {
      const maxHand = getMaxHandSize(state, side, catalog);
      if (ps.hand.length > maxHand) {
        for (const handId of ps.hand) {
          actions.push({ type: 'DISCARD_CARD', instanceId: handId });
        }
      }
      break;
    }

    case 'counter': {
      if (!state.combatState) {
        actions.push({ type: 'CONFIRM_BLOCKERS' });
        break;
      }

      // Assign blockers to groups
      const eligibleBlockers = ops.ring
        .filter(s => s.instanceId !== null)
        .map(s => s.instanceId!)
        .filter(id => {
          const inst = getInstance(state, id);
          return !inst.isExhausted;
        });

      for (let gIdx = 0; gIdx < state.combatState.groups.length; gIdx++) {
        if (state.combatState.blockerAssignments.has(gIdx)) continue;
        for (const blockerId of eligibleBlockers) {
          if (canAssignBlocker(state, gIdx, blockerId, catalog)) {
            actions.push({ type: 'ASSIGN_BLOCKER', groupIndex: gIdx, blockerInstanceId: blockerId });
          }
        }
      }

      // Unassign existing blockers
      for (const [, blockerId] of state.combatState.blockerAssignments) {
        actions.push({ type: 'UNASSIGN_BLOCKER', blockerInstanceId: blockerId });
      }

      // Play reactions
      for (const handId of ops.hand) {
        if (canPlayReaction(state, handId, catalog)) {
          actions.push({ type: 'PLAY_REACTION', instanceId: handId });
        }
      }

      // Confirm blockers (always available)
      actions.push({ type: 'CONFIRM_BLOCKERS' });
      break;
    }
  }

  return actions;
}
